import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { IconThumbDown } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Pill } from '@/components/pills/Pill'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'
import { submitFeedback } from '@/reducers/kodi/kodi.actions'
import { FEEDBACK_CATEGORIES, MAX_FEEDBACK_COMMENT_LENGTH } from '../kodi.constants'

// Memoized: one instance renders per assistant turn and its props are stable per
// message, so it shouldn't re-render on unrelated streaming/keystroke churn.
export const KodiFeedbackButton = React.memo(({ auditUuid, feedbackType }) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [categories, setCategories] = useState([])
  const [detail, setDetail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // A replayed turn that already carries feedback restores the disabled state.
  const [submitted, setSubmitted] = useState(Boolean(feedbackType))
  const commentId = `kodi-feedback-detail-${auditUuid}`
  const atLimit = detail.length >= MAX_FEEDBACK_COMMENT_LENGTH

  if (submitted) {
    return (
      <Button
        small
        square
        basic
        disabled
        disabledTooltip="Feedback sent"
        tooltipProps={{ placement: 'bottom' }}
        className="kodi-feedback-button--sent"
        aria-label="Feedback submitted"
      >
        <IconThumbDown />
      </Button>
    )
  }

  const toggleCategory = (category) => {
    setCategories((current) =>
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category]
    )
  }

  const closeModal = () => {
    setOpen(false)
    setCategories([])
    setDetail('')
  }

  // Only flip to the sent state when the write succeeds; on failure the action
  // toasts and the modal stays open (selections intact) for a retry.
  const handleSubmit = async () => {
    setSubmitting(true)
    const ok = await dispatch(submitFeedback(auditUuid, 'negative', categories, detail))
    setSubmitting(false)
    if (ok) {
      setSubmitted(true)
      closeModal()
    }
  }

  return (
    <>
      <Button small square basic aria-label="Bad response" onClick={() => setOpen(true)}>
        <IconThumbDown />
      </Button>
      <BasicModal
        show={open}
        size="tiny"
        title="What went wrong?"
        onClose={closeModal}
        footer={
          <div className="flex-align-center flex-end small-gap">
            <Button onClick={closeModal}>Cancel</Button>
            <Button primary loading={submitting} onClick={handleSubmit}>
              Submit feedback
            </Button>
          </div>
        }
      >
        <div className="kodi-feedback-modal">
          <p className="kodi-feedback-modal__subtitle muted-text">
            Your feedback helps Kodi improve. Pick anything that applies.
          </p>
          <div className="kodi-feedback-modal__categories">
            {FEEDBACK_CATEGORIES.map((category) => (
              <Pill
                key={category}
                clickable
                brand={categories.includes(category)}
                selected={categories.includes(category)}
                onClick={() => toggleCategory(category)}
              >
                {category}
              </Pill>
            ))}
          </div>
          <FormField error={atLimit}>
            <label htmlFor={commentId}>Tell us more (optional)</label>
            <TextArea
              id={commentId}
              rows={4}
              error={atLimit}
              maxLength={MAX_FEEDBACK_COMMENT_LENGTH}
              value={detail}
              onChange={(event) => setDetail(event.target.value)}
              placeholder="What did you expect Kodi to do?"
            />
            <span className="kodi-feedback-modal__count muted-text small-text">
              {detail.length} / {MAX_FEEDBACK_COMMENT_LENGTH}
            </span>
          </FormField>
          {atLimit && (
            <ErrorMessage
              content={`You've reached the ${MAX_FEEDBACK_COMMENT_LENGTH} character limit.`}
            />
          )}
        </div>
      </BasicModal>
    </>
  )
})

KodiFeedbackButton.displayName = 'KodiFeedbackButton'
